import { useState, useRef, useEffect } from "react"
import {
    Moon,
    Sun,
    Search,
    Send,
    Paperclip,
    ImageIcon,
    Smile,
    Calendar,
    Pin,
    Reply,
    Settings,
    MessageSquarePlus,
    Hash,
    Bell,
    ChevronDown,
    Plus,
    AtSign,
    Menu,
    X,
    ArrowLeft,
} from "lucide-react"
import { SettingsModal } from "../components/chat2/settings-modal"
import { ThreadView } from "../components/chat2/thread-view"
import { FileUpload } from "../components/chat2/file-upload"
import { MessageMenu } from "../components/chat2/message-menu"
import { GifPicker } from "../components/chat2/gif-picker"
import { CalendarPicker } from "../components/chat2/calendar-picker"
import { ToastContainer } from "../components/chat2/toast-container"
import { defaultSettings, loadSettings, saveSettings } from "../lib/settings"
// import { playSound } from "@/lib/utils"
import { isValidUrl } from "../lib/url-utils"
import { LinkPreview } from "../components/chat2/link-preview"
import { EmojiPicker } from "../components/chat2/emoji-picker"
import useChat from "../hooks/useChat"
import { useNavigate, useParams } from "react-router-dom"
import useAuth from "../hooks/useAuth"
import { formatMessageTime, formatLastActive } from "../utils/dateUtils"

export default function ChatInterface() {

    const authCtx = useAuth();
    const chatCtx = useChat();

    const [isDark, setIsDark] = useState(false)
    const [message, setMessage] = useState("")
    const [selectedChat, setSelectedChat] = useState(null)
    const [isResizing, setIsResizing] = useState(false)
    const [channelWidth, setChannelWidth] = useState(320)
    const [showDirectMessages, setShowDirectMessages] = useState(true)
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
    const [isMobileChannelOpen, setIsMobileChannelOpen] = useState(false)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const [showSettings, setShowSettings] = useState(false)
    const [showThreadView, setShowThreadView] = useState(null)
    const [showFileUpload, setShowFileUpload] = useState(false)
    const [editingMessage, setEditingMessage] = useState(null)
    const [editText, setEditText] = useState("")
    const [searchQuery, setSearchQuery] = useState("")
    const [pinnedChannels, setPinnedChannels] = useState([])
    const [notifications, setNotifications] = useState({})
    const [toasts, setToasts] = useState([])
    const [showGifPicker, setShowGifPicker] = useState(false)
    const [showCalendarPicker, setShowCalendarPicker] = useState(false)
    const [settings, setSettings] = useState(defaultSettings)
    const { userId } = useParams();
    const navigate = useNavigate();

    const [media, setMedia] = useState([]);
    const [mediaPreview, setMediaPreview] = useState([]);
    const [error, setError] = useState(null);

    // useEffect(() => {
    //     setSelectedChat(chatCtx.activeConversation);
    // }, [chatCtx.activeConversation]);

    useEffect(() => {
        if (userId) {
            setMessages([]);
            chatCtx.fetchMessages(userId);
            setSelectedChat(userId);
        }
        else {
            chatCtx.setActiveConversation(null);
        }
    }, [userId]);

    // Define channels and directMessages states first
    const [channels, setChannels] = useState([
        { id: "team-chat", name: "Team Chat", unread: 3, mentions: 1, isPinned: true },
        { id: "general", name: "General", unread: 0, mentions: 0, isPinned: true },
        { id: "design", name: "Design", unread: 5, mentions: 2 },
        { id: "development", name: "Development", unread: 1, mentions: 0 },
    ])

    const [directMessages, setDirectMessages] = useState(chatCtx.conversations);

    useEffect(() => {
        setDirectMessages(chatCtx.conversations);
    }, [chatCtx.conversations]);


    // const [directMessages, setDirectMessages] = useState([
    //     {
    //         id: "alice",
    //         name: "beyond",
    //         avatar: "/placeholder.svg?height=40&width=40",
    //         status: "online",
    //         lastSeen: "Active now",
    //         unread: 2,
    //     },
    //     {
    //         id: "round",
    //         name: "round",
    //         avatar: "/placeholder.svg?height=40&width=40",
    //         status: "offline",
    //         lastSeen: "2h ago",
    //         unread: 0,
    //     },
    //     {
    //         id: "elon",
    //         name: "elon",
    //         avatar: "/placeholder.svg?height=40&width=40",
    //         status: "idle",
    //         lastSeen: "30m ago",
    //         unread: 5,
    //     },
    // ])

    // Then initialize filteredChannels and filteredDMs
    const [filteredChannels, setFilteredChannels] = useState(channels)
    const [filteredDMs, setFilteredDMs] = useState(directMessages)

    const resizeRef = useRef(null)
    const startResizeX = useRef(0)
    const startWidth = useRef(0)

    // const resizeRef = null;
    // const startResizeX = { current: 0 };
    // const startWidth = { current: 0 };

    const toggleTheme = () => {
        const newTheme = isDark ? "light" : "dark"
        setIsDark(!isDark)
        const newSettings = {
            ...settings,
            theme: newTheme
        }
        setSettings(newSettings)
        saveSettings(newSettings)
        document.documentElement.classList.toggle("dark", !isDark)
    }

    const handleMouseDown = (e) => {
        e.preventDefault()
        setIsResizing(true)
        startResizeX.current = e.clientX
        startWidth.current = channelWidth
        document.body.style.cursor = "col-resize"
        document.body.style.userSelect = "none"
    }

    const handleTouchStart = (e) => {
        e.preventDefault()
        setIsResizing(true)
        startResizeX.current = e.touches[0].clientX
        startWidth.current = channelWidth
        document.body.style.userSelect = "none"
    }

    //   const handleMouseMove = useRef((e) => {
    //     if (!isResizing) return
    //     const diff = e.clientX - startResizeX.current
    //     const newWidth = Math.max(280, Math.min(480, startWidth.current + diff))
    //     setChannelWidth(newWidth)
    //   })
    const handleMouseMove = (e) => {
        if (!isResizing) return
        const diff = e.clientX - startResizeX.current
        const newWidth = Math.max(280, Math.min(480, startWidth.current + diff))
        setChannelWidth(newWidth)
    };

    //   const handleTouchMove = useRef((e) => {
    //     if (!isResizing) return
    //     const diff = e.touches[0].clientX - startResizeX.current
    //     const newWidth = Math.max(280, Math.min(480, startWidth.current + diff))
    //     setChannelWidth(newWidth)
    //   })


    const handleTouchMove = (e) => {
        if (!isResizing) return
        const diff = e.touches[0].clientX - startResizeX.current
        const newWidth = Math.max(280, Math.min(480, startWidth.current + diff))
        setChannelWidth(newWidth)
    }


    //   const handleMouseUp = useRef(() => {
    //     setIsResizing(false)
    //     document.body.style.cursor = ""
    //     document.body.style.userSelect = ""
    //     document.removeEventListener("mousemove", handleMouseMove.current)
    //     document.removeEventListener("touchmove", handleTouchMove.current)
    //     document.removeEventListener("mouseup", handleMouseUp.current)
    //     document.removeEventListener("touchend", handleMouseUp.current)
    //   })


    const handleMouseUp = () => {
        setIsResizing(false)
        document.body.style.cursor = ""
        document.body.style.userSelect = ""
        document.removeEventListener("mousemove", handleMouseMove.current)
        document.removeEventListener("touchmove", handleTouchMove.current)
        document.removeEventListener("mouseup", handleMouseUp.current)
        document.removeEventListener("touchend", handleMouseUp.current)
    }

    //   const handleTouchUp = useRef(() => {
    //     setIsResizing(false)
    //     document.body.style.userSelect = ""
    //     document.body.style.cursor = ""
    //     document.removeEventListener("mousemove", handleMouseMove.current)
    //     document.removeEventListener("touchmove", handleTouchMove.current)
    //     document.removeEventListener("mouseup", handleMouseUp.current)
    //     document.removeEventListener("touchend", handleTouchUp.current)
    //   })

    const handleTouchUp = () => {
        setIsResizing(false)
        document.body.style.userSelect = ""
        document.body.style.cursor = ""
        document.removeEventListener("mousemove", handleMouseMove.current)
        document.removeEventListener("touchmove", handleTouchMove.current)
        document.removeEventListener("mouseup", handleMouseUp.current)
        document.removeEventListener("touchend", handleTouchUp.current)
    }

    useEffect(() => {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove.current)
            document.addEventListener("touchmove", handleTouchMove.current)
            document.addEventListener("mouseup", handleMouseUp.current)
            document.addEventListener("touchend", handleTouchUp.current)
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove.current)
            document.removeEventListener("touchmove", handleTouchMove.current)
            document.removeEventListener("mouseup", handleMouseUp.current)
            document.removeEventListener("touchend", handleTouchUp.current)
        }
    }, [isResizing])

    // Close mobile navigation when screen size changes
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setIsMobileNavOpen(false)
                setIsMobileChannelOpen(false)
            }
        }
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    // Prevent body scroll when mobile nav is open
    useEffect(() => {
        if (isMobileNavOpen || isMobileChannelOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = ""
        }
    }, [isMobileNavOpen, isMobileChannelOpen])

    const handleChatSelect = (chatId) => {
        // setSelectedChat(chatId)
        navigate(`/chat/${chatId}`);
        setIsMobileNavOpen(false);
    }


    const [messages, setMessages] = useState([
        // {
        //     id: 1,
        //     user: "beyond",
        //     avatar: "/placeholder.svg?height=40&width=40",
        //     message:
        //         "Just pushed the latest design updates to Figma! 🎨 Check out the new components and let me know what you think. I've also added some micro-interactions that should make the user experience more engaging.",
        //     time: "10:30 AM",
        //     isOnline: true,
        //     reactions: [
        //         { emoji: "👍", count: 3, users: ["beyond", "round", "elon"] },
        //         { emoji: "🎨", count: 2, users: ["round", "elon"] },
        //     ],
        //     attachments: [
        //         {
        //             type: "image",
        //             url: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Screenshot%202025-02-20%20at%2021.01.28-St7T4xGDBgZ1cVtNuy5zbH4p0ECdYG.png",
        //             preview: "Design Preview",
        //         },
        //     ],
        //     thread: 2,
        // },
        // {
        //     id: 2,
        //     user: "round",
        //     avatar: "/placeholder.svg?height=40&width=40",
        //     message:
        //         "The new design system looks fantastic! I especially love the color palette you've chosen. Have you considered how it will work with our dark mode implementation?",
        //     time: "10:32 AM",
        //     isOnline: true,
        //     isReply: true,
        //     replyTo: "beyond",
        //     reactions: [{ emoji: "💡", count: 4, users: ["beyond", "round", "elon", "You"] }],
        //     thread: 2,
        // },
        // {
        //     id: 3,
        //     user: "elon",
        //     avatar: "/placeholder.svg?height=40&width=40",
        //     message:
        //         "📅 Team Sync Meeting\nTime: 2:00 PM EST\nDuration: 1 hour\nAgenda:\n- Sprint Review\n- Design System Updates\n- Q&A",
        //     time: "10:35 AM",
        //     isOnline: false,
        //     type: "event",
        //     reactions: [{ emoji: "✅", count: 5, users: ["beyond", "round", "elon", "You", "alice"] }],
        // },
    ])

    useEffect(() => {
        console.log("CM : ", chatCtx.messages)
        setMessages(chatCtx.messages);
    }, [chatCtx.messages]);


    // Handle content change
    const handleContentChange = (e) => {
        setMessage(e.target.value);
        if ((e.target.value || '').length > 0) {
            if (userId) {
                chatCtx.sendTypingStart(userId);
            }
        }
        else {
            if (userId) {
                chatCtx.sendTypingStop(userId);
            }
        }
    };

    // Handle Enter key press to send message
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Message handling
    // const handleSendMessage = async () => {
    //     if (!message.trim()) return;
    //     if (!userId) return;

    //     // Check for URLs in the message
    //     const words = message.split(" ")
    //     const urls = words.filter((word) => isValidUrl(word))

    //     const newMessage = {
    //         id: messages.length + 1,
    //         user: "You",
    //         avatar: "/placeholder.svg?height=40&width=40",
    //         message: message,
    //         time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    //         // isOnline: true,
    //         // attachments: urls.map((url) => ({
    //         //     type: "link",
    //         //     url,
    //         // })),
    //     }

    //     setMessages([...messages, newMessage])
    //     setMessage("")

    //     // Play message sound if enabled
    //     const currentSettings = loadSettings()
    //     if (currentSettings.sound.messageSound) {
    //         //   playSound("message")
    //     }
    // }

    const handleSendGifMessage = async (gif) => {
        if (!userId) return;

        // let media = m;

        // if (!message.trim() && media.length === 0) {
        //     //   setError('Please enter a message or add media');
        //     return;
        // }

        try {
            //   setIsSubmitting(true);
            //   setError('');

            chatCtx.sendTypingStop(userId);
            console.log("G : ", gif)

            const messageData = {
                content: message.trim() || "",
                attachements: [{
                    type: "image",
                    url: gif,
                    preview: "gif"
                }],
            };

            // await chatCtx.sendNewMessageHttp(userId, messageData);
            await chatCtx.sendMessage(userId, messageData);

            // Clear input after sending
            setMessage('');
            setMedia([]);
            setMediaPreview([]);
        } catch (error) {
            console.error('Error sending message:', error);
            //   setError('Failed to send message. Please try again.');
        } finally {
            //   setIsSubmitting(false);
        }

    }

    const handleSendMessage = async () => {
        if (!userId) return;

        // let media = m;

        if (!message.trim() && media.length === 0) {
            //   setError('Please enter a message or add media');
            return;
        }

        try {
            //   setIsSubmitting(true);
            //   setError('');

            chatCtx.sendTypingStop(userId);

            const messageData = {
                content: message.trim(),
                media: media,
                attachements: []
            };

            if (media.length > 0) {
                await chatCtx.sendNewMessageHttp(userId, messageData);
            }
            else {
                await chatCtx.sendMessage(userId, messageData);
            }

            // Clear input after sending
            setMessage('');
            setMedia([]);
            setMediaPreview([]);
        } catch (error) {
            console.error('Error sending message:', error);
            //   setError('Failed to send message. Please try again.');
        } finally {
            //   setIsSubmitting(false);
        }
    };

    const handleEditMessage = (messageId) => {
        const messageToEdit = messages.find((m) => m.id === messageId)
        if (messageToEdit) {
            setEditingMessage(messageId)
            setEditText(messageToEdit.message)
        }
    }

    const saveEdit = (messageId) => {
        setMessages(
            messages.map((m) =>
                m.id === messageId ? { ...m, message: editText, edited: true, editedAt: new Date().toLocaleTimeString() } : m,
            ),
        )
        setEditingMessage(null)
        setEditText("")
    }

    const handleDeleteMessage = (messageId) => {
        setMessages(messages.filter((m) => m.id !== messageId))
    }

    // Channel management
    const handleCreateChannel = () => {
        const channelName = prompt("Enter channel name:")
        if (channelName) {
            const newChannel = {
                id: `channel-${channels.length + 1}`,
                name: channelName,
                unread: 0,
                mentions: 0,
                isPinned: false,
            }
            setChannels([...channels, newChannel])
        }
    }

    const togglePinChannel = (channelId) => {
        setChannels(channels.map((c) => (c.id === channelId ? { ...c, isPinned: !c.isPinned } : c)))
    }

    const toggleChannelNotification = (channelId) => {
        setNotifications((prev) => ({
            ...prev,
            [channelId]: !prev[channelId],
        }))
    }

    // Search functionality
    useEffect(() => {
        if (searchQuery) {
            setFilteredChannels(channels.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase())))
            setFilteredDMs(directMessages.filter((dm) => dm.name.toLowerCase().includes(searchQuery.toLowerCase())))
        } else {
            setFilteredChannels(channels)
            setFilteredDMs(directMessages)
        }
    }, [searchQuery, channels, directMessages])

    // File handling
    const handleFileUpload = (files) => {
        // Process uploaded files
        // files.forEach((file) => {
        //     const isImage = file.type.startsWith("image/")
        //     const newMessage = {
        //         id: messages.length + 1,
        //         user: "You",
        //         avatar: "/placeholder.svg?height=40&width=40",
        //         message: `Shared ${isImage ? "an image" : "a file"}: ${file.name}`,
        //         time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        //         isOnline: true,
        //         attachments: [
        //             {
        //                 type: isImage ? "image" : "file",
        //                 url: URL.createObjectURL(file),
        //                 name: file.name,
        //                 size: file.size,
        //             },
        //         ],
        //     }
        //     setMessages((prev) => [...prev, newMessage])
        // })

        if (files.length === 0) return;

        // Validate file types and sizes
        const allowedTypes = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'video/mp4',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];
        const maxSize = 5 * 1024 * 1024; // 5MB


        const validFiles = files.filter((file) => {
            // Check file type
            if (!allowedTypes.includes(file.type)) {
                // setError(`File type not supported: ${file.type}`);
                return false;
            }

            // Check file size
            if (file.size > maxSize) {
                // setError(`File too large: ${(file.size / (1024 * 1024)).toFixed(2)}MB (max 5MB)`);
                return false;
            }

            return true;
        });

        // Update media and previews
        setMedia([...media, ...validFiles]);

        // Create previews
        const newPreviews = validFiles.map((file) => {
            return {
                file,
                url: URL.createObjectURL(file),
                type: file.type.startsWith('image/') ? 'image' :
                    file.type.startsWith('video/') ? 'video' : 'document',
            };
        });


        setMediaPreview([...mediaPreview, ...newPreviews]);
    }

    // Emoji handling
    const handleEmojiSelect = (emoji) => {
        setMessage((prev) => prev + emoji)
        setShowEmojiPicker(false)
    }

    const addReaction = (messageId, emoji) => {
        setMessages(
            messages.map((m) => {
                if (m.id === messageId) {
                    const existingReaction = m.reactions?.find((r) => r.emoji === emoji)
                    const newReactions = m.reactions || []

                    if (existingReaction) {
                        // Check if user has already reacted
                        if (existingReaction.users.includes("You")) {
                            // Remove user's reaction
                            return {
                                ...m,
                                reactions: newReactions
                                    .map((r) =>
                                        r.emoji === emoji
                                            ? {
                                                ...r,
                                                count: r.count - 1,
                                                users: r.users.filter((u) => u !== "You"),
                                            }
                                            : r,
                                    )
                                    .filter((r) => r.count > 0), // Remove reaction if count reaches 0
                            }
                        } else {
                            // Add user's reaction
                            return {
                                ...m,
                                reactions: newReactions.map((r) =>
                                    r.emoji === emoji
                                        ? {
                                            ...r,
                                            count: r.count + 1,
                                            users: [...r.users, "You"],
                                        }
                                        : r,
                                ),
                            }
                        }
                    } else {
                        // Create new reaction
                        return {
                            ...m,
                            reactions: [...newReactions, { emoji, count: 1, users: ["You"] }],
                        }
                    }
                }
                return m
            }),
        )
    }

    // Calendar integration
    const handleScheduleMessage = (date) => {
        const formattedDate = date.toLocaleString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
        })

        const newMessage = {
            id: messages.length + 1,
            user: "You",
            avatar: "/placeholder.svg?height=40&width=40",
            message: `📅 Scheduled Meeting\nTime: ${formattedDate}\nDuration: 1 hour\nStatus: Scheduled`,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            isOnline: true,
            type: "event",
            reactions: [],
        }

        setMessages([...messages, newMessage])
        setShowCalendarPicker(false)
        addToast(`Meeting scheduled for ${formattedDate}`, "success")
    }

    const addToast = (message, type) => {
        const id = Math.random().toString(36).substr(2, 9)
        setToasts((prev) => [...prev, { id, message, type }])
        setTimeout(() => removeToast(id), 3000)
    }

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id))
    }

    useEffect(() => {
        // Load settings on component mount
        const loadedSettings = loadSettings()
        setSettings(loadedSettings)

        // Apply initial theme
        // const isDarkTheme =
        //     loadedSettings.theme === "dark" ||
        //     (loadedSettings.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)

        const isDarkTheme = false;
        setIsDark(isDarkTheme)
        document.documentElement.classList.toggle("dark", isDarkTheme)

        // Apply chat density
        document.documentElement.style.setProperty(
            "--message-spacing",
            loadedSettings.chatDensity === "compact" ? "0.75rem" : "1.5rem",
        )
    }, [])

    // return (<></>)

    return (
        <div className={`flex bg-[#f8fafc]  ${isDark ? "dark" : ""}`} style={{ height: `calc(100vh - 65px)` }}>
            {/* Mobile Navigation Overlay */}
            {isMobileNavOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsMobileNavOpen(false)} />
            )}

            {/* Mobile Channel List Overlay */}
            {isMobileChannelOpen && (
                <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setIsMobileChannelOpen(false)} />
            )}

            {/* Navigation Sidebar */}
            {/* <div
                className={`
        fixed lg:relative w-72 lg:w-20 h-full bg-white  border-r border-gray-200 
         flex flex-col z-50 transition-transform duration-300 ease-in-out
        ${isMobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
            >
                <div className="flex lg:flex-col items-center justify-between lg:justify-start p-4 lg:py-4 lg:gap-6">
                    <button className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center font-bold text-xl">
                        VZ
                    </button>
                    <button
                        className="lg:hidden p-2 rounded-lg hover:bg-gray-100 "
                        onClick={() => setIsMobileNavOpen(false)}
                    >
                        <X className="w-6 h-6 text-gray-600 " />
                    </button>
                </div>

                <div className="flex flex-col lg:items-center gap-2 p-2 lg:p-0">
                    <button className="w-full lg:w-12 h-12 rounded-xl lg:rounded-2xl bg-gray-100  hover:bg-gray-200  flex items-center lg:justify-center px-4 lg:px-0 gap-4 lg:gap-0">
                        <MessageSquarePlus className="w-6 h-6 text-gray-600 " />
                        <span className="lg:hidden">New Chat</span>
                    </button>
                    <button
                        className={`w-full lg:w-12 h-12 rounded-xl lg:rounded-2xl hover:bg-gray-200  flex items-center lg:justify-center px-4 lg:px-0 gap-4 lg:gap-0 ${showDirectMessages ? "bg-gray-100 " : "bg-purple-100 "
                            }`}
                        onClick={() => setShowDirectMessages(false)}
                    >
                        <Hash
                            className={`w-6 h-6 ${showDirectMessages ? "text-gray-600 " : "text-purple-500 "
                                }`}
                        />
                        <span className="lg:hidden">Channels</span>
                    </button>
                    <button
                        className={`w-full lg:w-12 h-12 rounded-xl lg:rounded-2xl hover:bg-gray-200  flex items-center lg:justify-center px-4 lg:px-0 gap-4 lg:gap-0 ${!showDirectMessages ? "bg-gray-100 " : "bg-purple-100 "
                            }`}
                        onClick={() => setShowDirectMessages(true)}
                    >
                        <AtSign
                            className={`w-6 h-6 ${!showDirectMessages ? "text-gray-600 " : "text-purple-500 "
                                }`}
                        />
                        <span className="lg:hidden">Direct Messages</span>
                    </button>
                </div>

                <div className="mt-auto flex lg:flex-col items-center gap-2 p-4 lg:gap-4">
                    <button
                        onClick={toggleTheme}
                        className="w-full lg:w-12 h-12 rounded-xl lg:rounded-2xl bg-gray-100  hover:bg-gray-200  flex items-center lg:justify-center px-4 lg:px-0 gap-4 lg:gap-0"
                    >
                        {isDark ? (
                            <>
                                <Sun className="w-6 h-6 text-gray-600 " />
                                <span className="lg:hidden">Light Mode</span>
                            </>
                        ) : (
                            <>
                                <Moon className="w-6 h-6 text-gray-600 " />
                                <span className="lg:hidden">Dark Mode</span>
                            </>
                        )}
                    </button>
                    <button
                        onClick={() => setShowSettings(true)}
                        className="w-full lg:w-12 h-12 rounded-xl lg:rounded-2xl bg-gray-100  hover:bg-gray-200  flex items-center lg:justify-center px-4 lg:px-0 gap-4 lg:gap-0"
                    >
                        <Settings className="w-6 h-6 text-gray-600 " />
                        <span className="lg:hidden">Settings</span>
                    </button>
                    <div className="w-12 h-12 rounded-2xl overflow-hidden hidden lg:block">
                        <img src="/placeholder.svg?height=48&width=48" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                </div>
            </div> */}

            {/* Channels/DMs Sidebar */}
            <div
                className={`
          fixed lg:relative w-full lg:w-auto h-full bg-white  border-r border-gray-200 
           flex z-40 transition-transform duration-300 ease-in-out
           ${isMobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
                style={{ width: isResizing ? channelWidth : undefined }}
            >
                <div className="flex-1 w-full lg:w-80">
                    <div className="flex items-center gap-2 p-4 border-b border-gray-200 ">
                        <button
                            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 "
                            onClick={() => setIsMobileNavOpen(false)}
                        >
                            <ArrowLeft className="w-6 h-6 text-gray-600 " />
                        </button>
                        <div className="flex-1 flex items-center justify-between">
                            <h1 className="text-xl font-bold ">
                                {showDirectMessages ? "Direct Messages" : "Channels"}
                            </h1>
                            {/* <button className="p-2 rounded-xl hover:bg-gray-100 " onClick={handleCreateChannel}>
                                <Plus className="w-5 h-5 text-gray-600 " />
                            </button> */}
                            <button
                                onClick={() => setShowSettings(true)}
                                className="w-full lg:w-12 h-12 rounded-xl lg:rounded-2xl bg-gray-100  hover:bg-gray-200  flex items-center lg:justify-center px-4 lg:px-0 gap-4 lg:gap-0"
                            >
                                <Settings className="w-6 h-6 text-gray-600 " />
                                <span className="lg:hidden">Settings</span>
                            </button>
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="relative">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={showDirectMessages ? "Search people..." : "Search channels..."}
                                className="w-full pl-10 pr-4 py-2 rounded-xl bg-gray-100  border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 "
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {showDirectMessages ? (
                        <div className="p-2">
                            {(searchQuery ? filteredDMs : directMessages).map((dm) => (
                                <button
                                    key={dm.otherParticipant._id}
                                    onClick={() => handleChatSelect(dm.otherParticipant._id)}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${selectedChat === dm.otherParticipant._id ? "bg-purple-50 " : "hover:bg-gray-50 "
                                        }`}
                                >
                                    <div className="relative">
                                        <img src={dm.otherParticipant.profilePicture || "/placeholder.svg"} alt={dm.name} className="w-10 h-10 rounded-xl" />
                                        <div
                                            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white  ${dm.otherParticipant.isOnline ? "bg-green-500" : "bg-gray-500"
                                                }`}
                                        />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <div
                                            className={`font-medium ${selectedChat === dm.id
                                                ? "text-purple-500 "
                                                : "text-gray-700 "
                                                }`}
                                        >
                                            {dm.otherParticipant.name}
                                        </div>
                                        <div className="text-sm text-gray-500 ">{dm.lastMessage.content ||
                                            (dm.lastMessage.media && dm.lastMessage.media.length > 0
                                                ? 'Sent media'
                                                : '')}</div>
                                    </div>
                                    {dm.unreadCount > 0 && (
                                        <span className="px-2 py-1 text-xs font-medium bg-purple-500 text-white rounded-full">
                                            {dm.unreadCount}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="p-2">
                            <div className="flex items-center justify-between p-2 text-sm font-medium text-gray-600 ">
                                <span>PINNED</span>
                                <ChevronDown className="w-4 h-4" />
                            </div>
                            {(searchQuery ? filteredChannels : channels)
                                .filter((c) => c.isPinned)
                                .map((channel) => (
                                    <div
                                        key={channel.id}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${selectedChat === channel.id
                                            ? "bg-purple-50 "
                                            : "hover:bg-gray-50 "
                                            }`}
                                    >
                                        <div
                                            className="flex-1 flex items-center gap-3 cursor-pointer"
                                            onClick={() => handleChatSelect(channel.id)}
                                        >
                                            <Hash
                                                className={`w-5 h-5 ${selectedChat === channel.id ? "text-purple-500" : "text-gray-400"}`}
                                            />
                                            <span
                                                className={`flex-1 font-medium ${selectedChat === channel.id
                                                    ? "text-purple-500 "
                                                    : "text-gray-700 "
                                                    }`}
                                            >
                                                {channel.name}
                                            </span>
                                            {channel.unread > 0 && (
                                                <span className="px-2 py-1 text-xs font-medium bg-purple-500 text-white rounded-full">
                                                    {channel.unread}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => togglePinChannel(channel.id)}
                                                className="p-2 rounded-xl hover:bg-gray-100 "
                                            >
                                                <Pin className="w-5 h-5 text-gray-600 " />
                                            </button>
                                            <button
                                                onClick={() => toggleChannelNotification(channel.id)}
                                                className="p-2 rounded-xl hover:bg-gray-100 "
                                            >
                                                <Bell className="w-5 h-5 text-gray-600 " />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            <div className="flex items-center justify-between p-2 mt-4 text-sm font-medium text-gray-600 ">
                                <span>CHANNELS</span>
                                <ChevronDown className="w-4 h-4" />
                            </div>
                            {(searchQuery ? filteredChannels : channels)
                                .filter((c) => !c.isPinned)
                                .map((channel) => (
                                    <div
                                        key={channel.id}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${selectedChat === channel.id
                                            ? "bg-purple-50 "
                                            : "hover:bg-gray-50 "
                                            }`}
                                    >
                                        <div
                                            className="flex-1 flex items-center gap-3 cursor-pointer"
                                            onClick={() => handleChatSelect(channel.id)}
                                        >
                                            <Hash
                                                className={`w-5 h-5 ${selectedChat === channel.id ? "text-purple-500" : "text-gray-400"}`}
                                            />
                                            <span
                                                className={`flex-1 font-medium ${selectedChat === channel.id
                                                    ? "text-purple-500 "
                                                    : "text-gray-700 "
                                                    }`}
                                            >
                                                {channel.name}
                                            </span>
                                            {channel.unread > 0 && (
                                                <span className="px-2 py-1 text-xs font-medium bg-purple-500 text-white rounded-full">
                                                    {channel.unread}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => togglePinChannel(channel.id)}
                                                className="p-2 rounded-xl hover:bg-gray-100 "
                                            >
                                                <Pin className="w-5 h-5 text-gray-600 " />
                                            </button>
                                            <button
                                                onClick={() => toggleChannelNotification(channel.id)}
                                                className="p-2 rounded-xl hover:bg-gray-100 "
                                            >
                                                <Bell className="w-5 h-5 text-gray-600 " />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Resize Handle - Only visible on desktop */}
                <div
                    className="hidden lg:block w-1 cursor-col-resize hover:bg-purple-500 active:bg-purple-500 transition-colors relative group"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    ref={resizeRef}
                >
                    <div className="absolute inset-y-0 -left-1 -right-1" />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-300  opacity-0 group-hover:opacity-100 pointer-events-none" />
                </div>
            </div>

            {/* Main Chat Area */}
            {selectedChat ? (
                <div className="flex-1 flex flex-col bg-white ">
                    {/* Chat Header */}
                    <div className="flex items-center gap-2 px-4 py-3 lg:px-6 lg:py-4 border-b border-gray-200 ">
                        <button
                            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 "
                            onClick={() => setIsMobileNavOpen(true)}
                        >
                            <Menu className="w-6 h-6 text-gray-600 " />
                        </button>
                        <div className="flex-1 flex items-center justify-between min-w-0">
                            <div className="flex items-center gap-4 min-w-0">
                                <button className="lg:hidden">
                                    {(
                                        <div className="relative">
                                            <img
                                                src={directMessages.find((dm) => dm.otherParticipant._id === selectedChat)?.otherParticipant.profilePicture || "/placeholder.svg"}
                                                alt="User"
                                                className="w-10 h-10 rounded-xl"
                                            />
                                            <div
                                                className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white  ${directMessages.find((dm) => dm.otherParticipant._id === selectedChat)?.otherParticipant.isOnline
                                                    ? "bg-green-500" : "bg-gray-500"
                                                    }`}
                                            />
                                        </div>
                                    )}
                                </button>
                                <div className="min-w-0">
                                    <h2 className="text-lg font-bold  truncate">
                                    {directMessages.find((dm) => dm.otherParticipant._id === selectedChat)?.otherParticipant.name || directMessages.find((dm) => dm.otherParticipant._id === selectedChat)?.otherParticipant.username}
                                    </h2>
                                    <p className="text-sm text-gray-500  truncate">
                                    {formatLastActive(directMessages.find((dm) => dm.otherParticipant._id === selectedChat)?.otherParticipant.lastActive)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center">
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="messages-container flex-1 overflow-y-auto p-4 lg:p-6">
                        {messages.map((msg) => (
                            <div key={msg._id} className="group">
                                <div className="flex items-start gap-4" style={{ marginBottom: "var(--message-spacing)" }}>
                                    <div className="relative shrink-0">
                                        <img src={msg.sender._id === authCtx.user.data.user._id ? authCtx.user.data.user.profilePicture : msg.sender.profilePicture} alt={msg.recipient.name} className="w-10 h-10 rounded-xl" />
                                        {/* {msg.isOnline && (
                                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white "></div>
                                    )} */}
                                    </div>
                                    <div className="flex-1 min-w-0">


                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold  truncate">{msg.sender.name}</span>
                                            <span className="text-xs text-gray-400 shrink-0">{formatMessageTime(msg.updatedAt || msg.createdAt)}</span>
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 ml-auto">
                                                <button
                                                    className="p-1 rounded-lg hover:bg-gray-100 "
                                                    onClick={() => setShowThreadView(msg)}
                                                >
                                                    <Reply className="w-4 h-4 text-gray-400" />
                                                </button>
                                                <MessageMenu
                                                    messageId={msg._id}
                                                    onEdit={() => handleEditMessage(msg.id)}
                                                    onDelete={() => handleDeleteMessage(msg.id)}
                                                    onPin={() => {
                                                        addToast("Message pinned successfully", "success")
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {msg.isReply && (
                                            <div className="ml-4 my-1 pl-2 border-l-2 border-gray-200 ">
                                                <p className="text-sm text-gray-500 ">Replying to {msg.replyTo}</p>
                                            </div>
                                        )}
                                        <div className="mt-1">
                                            {editingMessage === msg._id ? (
                                                <div className="flex items-center gap-2 bg-gray-100  p-2 rounded-xl">
                                                    <input
                                                        type="text"
                                                        value={editText}
                                                        onChange={(e) => setEditText(e.target.value)}
                                                        className="flex-1 px-4 py-2 bg-transparent border-0 focus:outline-none focus:ring-0  placeholder-gray-500"
                                                    />
                                                    <button
                                                        onClick={() => saveEdit(msg._id)}
                                                        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg"
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="text-gray-700  whitespace-pre-line break-words">
                                                        {msg.content}
                                                    </p>
                                                    {msg.media?.map((attachment, index) => (
                                                        <div key={index} className="mt-2">
                                                            {attachment.type === "image" && (
                                                                <div className="relative group rounded-xl overflow-hidden">
                                                                    <img
                                                                        src={attachment.url || "/placeholder.svg"}
                                                                        alt={attachment.preview}
                                                                        className="max-w-full lg:max-w-md rounded-xl border border-gray-200 "
                                                                    />
                                                                    {/* <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200" /> */}
                                                                </div>
                                                            )}
                                                            {attachment.type === "link" && <LinkPreview url={attachment.url} isExpanded={true} />}
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                        {/* {msg.reactions && msg.reactions.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                            {msg.reactions.map((reaction, index) => (
                                                <button
                                                    key={index}
                                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${reaction.users.includes("You")
                                                        ? "bg-purple-100  text-purple-600 "
                                                        : "bg-gray-100  hover:bg-gray-200 "
                                                        }`}
                                                    onClick={() => addReaction(msg.id, reaction.emoji)}
                                                >
                                                    <span>{reaction.emoji}</span>
                                                    <span
                                                        className={`text-xs ${reaction.users.includes("You")
                                                            ? "text-purple-600 "
                                                            : "text-gray-500 "
                                                            }`}
                                                    >
                                                        {reaction.count}
                                                    </span>
                                                </button>
                                            ))}
                                            <button
                                                className="p-1 rounded-full hover:bg-gray-100 "
                                                onClick={() => setShowEmojiPicker(true)}
                                            >
                                                <Smile className="w-4 h-4 text-gray-400" />
                                            </button>
                                        </div>
                                    )}
                                    {msg.thread && (
                                        <button className="mt-2 text-sm text-purple-500 hover:text-purple-600  ">
                                            {msg.thread} replies
                                        </button>
                                    )} */}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Input Area */}
                    {chatCtx.activeConversation && Object.keys(chatCtx.typingUsers).length > 0 && <div className="text-gray-500  px-4 py-2">
                        {chatCtx.typingUsers[chatCtx.activeConversation.recipient._id] ? `${chatCtx.activeConversation.recipient.username} is typing...` : ''}
                    </div>}




                    {mediaPreview.length > 0 && (
                        <div className="px-4 py-2 flex flex-wrap gap-4 border-t border-gray-200 ">
                            {mediaPreview.map((preview, index) => (
                                <div key={index} className="relative">
                                    {preview.type === 'image' && (
                                        <img
                                            src={preview.url}
                                            alt="preview"
                                            className="h-24 w-24 object-cover rounded-lg"
                                        />
                                    )}
                                    {preview.type === 'video' && (
                                        <video
                                            src={preview.url}
                                            className="h-24 w-24 object-cover rounded-lg"
                                            controls
                                        />
                                    )}
                                    {preview.type === 'document' && (
                                        <div className="h-24 w-24 flex items-center justify-center bg-gray-200  text-sm text-gray-700  rounded-lg">
                                            Document
                                        </div>
                                    )}
                                    <button
                                        onClick={() => {
                                            const updated = [...mediaPreview];
                                            updated.splice(index, 1);
                                            setMediaPreview(updated);
                                        }}
                                        className="absolute top-0 right-0 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}


                    <div className="p-4 border-t border-gray-200 ">
                        <div className="flex items-center gap-2 bg-gray-100  p-2 rounded-xl">
                            <div className="flex gap-1">
                                <button
                                    className="p-2 rounded-lg hover:bg-white "
                                    onClick={() => setShowFileUpload(true)}
                                >
                                    <Paperclip className="w-5 h-5 text-gray-500" />
                                </button>
                                {/* <button className="p-2 rounded-lg hover:bg-white  hidden sm:flex">
                                    <ImageIcon className="w-5 h-5 text-gray-500" />
                                </button> */}
                            </div>


                            <input
                                type="text"
                                value={message}
                                onChange={handleContentChange}
                                onKeyDown={handleKeyDown}
                                // onChange={(e) => setMessage(e.target.value)}
                                // onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                                placeholder="Type a message..."
                                className="flex-1 px-4 py-2 bg-transparent border-0 focus:outline-none focus:ring-0  placeholder-gray-500"
                            />
                            <div className="flex gap-1">
                                {/* <button
                                    className="p-2 rounded-lg hover:bg-white  hidden sm:flex"
                                    onClick={() => setShowCalendarPicker(true)}
                                >
                                    <Calendar className="w-5 h-5 text-gray-500" />
                                </button> */}
                                <button
                                    className="p-2 rounded-lg hover:bg-white "
                                    onClick={() => setShowGifPicker(true)}
                                >
                                    <ImageIcon className="w-5 h-5 text-gray-500" />
                                </button>
                                <button
                                    className="p-2 rounded-lg hover:bg-white "
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setShowEmojiPicker(true)
                                    }}
                                >
                                    <Smile className="w-5 h-5 text-gray-500" />
                                </button>
                                <button
                                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2"
                                    onClick={handleSendMessage}
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>


                </div>
            ) : (
                <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }} className="flex-1 flex flex-col bg-white ">
                        <h1 style={{ color: "gray" }}>Please Select a Chat to start messaging</h1>
                    </div>
                </>
            )}

            {showEmojiPicker && (
                <EmojiPicker
                    onSelect={(emoji) => {
                        setMessage((prev) => prev + emoji)
                    }}
                    onClose={() => setShowEmojiPicker(false)}
                />
            )}

            {showSettings && <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />}

            {showThreadView && <ThreadView message={showThreadView} onClose={() => setShowThreadView(null)} />}

            {showFileUpload && <FileUpload onUpload={handleFileUpload} onClose={() => setShowFileUpload(false)} />}

            {showGifPicker && (
                <GifPicker
                    onSelect={(gif) => {
                        // const newMessage = {
                        //     id: messages.length + 1,
                        //     user: "You",
                        //     avatar: "/placeholder.svg?height=40&width=40",
                        //     message: "",
                        //     time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                        //     isOnline: true,
                        //     attachments: [
                        //         {
                        //             type: "image",
                        //             url: gif,
                        //             preview: "GIF",
                        //         },
                        //     ],
                        // }
                        // setMessages([...messages, newMessage])
                        // setShowGifPicker(false)
                        // addToast("GIF sent successfully", "success")
                        handleSendGifMessage(gif);
                        setShowGifPicker(false)
                    }}
                    onClose={() => setShowGifPicker(false)}
                />
            )}

            {showCalendarPicker && (
                <CalendarPicker
                    onSelect={(date) => {
                        handleScheduleMessage(date)
                        setShowCalendarPicker(false)
                        addToast(`Message scheduled for ${date.toLocaleString()}`, "success")
                    }}
                    onClose={() => setShowCalendarPicker(false)}
                />
            )}

            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
    )

    function applyTheme(theme) {
        if (theme === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
            document.documentElement.classList.toggle("dark", systemTheme === "dark")
        } else {
            document.documentElement.classList.toggle("dark", theme === "dark")
        }
    }
}

